/**
 * Auth Store - Zustand store for authentication state
 *
 * This store holds auth state that needs to be accessible outside React components
 * (e.g., from Yjs callbacks, API interceptors). The AuthProvider component syncs
 * Better Auth's useSession() data into this store.
 *
 * Auth API methods (signin, signup, etc.) are also here since they need to
 * read/write store state and are called from components.
 */

import { create } from 'zustand';
import {
  authClient,
  authFetch,
  listSessions as _listSessions,
  revokeSession as _revokeSession,
  revokeOtherSessions as _revokeOtherSessions,
  revokeSessions as _revokeSessions,
} from '@/api/auth-client';
import { deleteMyAccount } from '@/server/functions/users.functions';
import { queryClient } from '@/lib/queryClient';
import { BASEPATH } from '@/config/api';
import { saveLastLoginMethod, LOGIN_METHODS } from '@/lib/lastLoginMethod';

// LocalStorage keys for offline caching
const AUTH_CACHE_KEY = 'corates-auth-cache';
const AUTH_CACHE_TIMESTAMP_KEY = 'corates-auth-cache-timestamp';
const AUTH_CACHE_MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days

// Providers run with disableImplicitSignUp; only the sign-up page sets this
interface SocialSignInOptions {
  requestSignUp?: boolean;
}

export interface AuthUser {
  id: string;
  email: string;
  emailVerified?: boolean | null;
  profileCompletedAt?: number | null;
  name?: string;
  givenName?: string;
  persona?: string;
  image?: string | null;
  role?: string | null;
  twoFactorEnabled?: boolean | null;
  // Raw JSON from the user.preferences column; read it with parseUserPreferences
  preferences?: string | null;
  [key: string]: unknown;
}

// Better Auth caches the session in a cookie for a few minutes, so a write
// made outside its own endpoints is invisible to a plain refetch until then
export interface SessionRefetchOptions {
  disableCookieCache?: boolean;
}
export type SessionRefetch = (options?: SessionRefetchOptions) => Promise<void>;

interface AuthState {
  isOnline: boolean;
  cachedUser: AuthUser | null;
  cachedAvatarUrl: string | null;
  authError: string | null;

  // These are synced from AuthProvider (Better Auth useSession)
  sessionUser: AuthUser | null;
  sessionLoading: boolean;
  // The last session check failed for a reason other than 401 (rate limit,
  // 5xx, network), so the server has not actually said "no session"
  sessionUnavailable: boolean;
  sessionRefetch: SessionRefetch | null;
}

interface AuthActions {
  setOnline: (online: boolean) => void;
  setCachedUser: (user: AuthUser | null) => void;
  setCachedAvatarUrl: (url: string | null) => void;
  setAuthError: (error: string | null) => void;
  setSessionData: (
    user: AuthUser | null,
    loading: boolean,
    refetch: SessionRefetch | null,
    unavailable?: boolean,
  ) => void;

  // Auth API methods
  signup: (email: string, password: string, name: string, role?: string | null) => Promise<unknown>;
  signin: (email: string, password: string) => Promise<{ twoFactorRequired: true } | unknown>;
  signinWithGoogle: (callbackPath?: string, options?: SocialSignInOptions) => Promise<unknown>;
  signinWithOrcid: (callbackPath?: string, options?: SocialSignInOptions) => Promise<unknown>;
  sendSignInCode: (email: string) => Promise<void>;
  signinWithEmailCode: (email: string, code: string) => Promise<unknown>;
  signout: () => Promise<void>;
  updateProfile: (data: Record<string, unknown>) => Promise<unknown>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  requestPasswordResetCode: (email: string) => Promise<void>;
  resetPasswordWithCode: (email: string, code: string, newPassword: string) => Promise<void>;
  sendVerificationCode: (email: string) => Promise<void>;
  verifyEmailWithCode: (email: string, code: string) => Promise<void>;
  requestOnboardingEmailCode: (email: string) => Promise<void>;
  confirmOnboardingEmail: (email: string, code: string) => Promise<{ claimed: boolean }>;
  deleteAccount: () => Promise<{ success: boolean }>;

  // 2FA
  enableTwoFactor: (password: string) => Promise<unknown>;
  verifyTwoFactorSetup: (code: string) => Promise<unknown>;
  disableTwoFactor: (password: string) => Promise<unknown>;
  verifyTwoFactor: (code: string) => Promise<unknown>;

  // Session management
  listActiveSessions: () => Promise<unknown[]>;
  revokeSessionByToken: (token: string) => Promise<void>;
  revokeAllOtherSessions: () => Promise<void>;
  revokeAllSessions: () => Promise<void>;
  forceRefreshSession: () => Promise<void>;
}

// Pure functions for localStorage cache

export function loadCachedAuth(): AuthUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const cached = localStorage.getItem(AUTH_CACHE_KEY);
    const timestamp = localStorage.getItem(AUTH_CACHE_TIMESTAMP_KEY);
    if (!cached || !timestamp) return null;

    const age = Date.now() - parseInt(timestamp, 10);
    if (age > AUTH_CACHE_MAX_AGE) {
      localStorage.removeItem(AUTH_CACHE_KEY);
      localStorage.removeItem(AUTH_CACHE_TIMESTAMP_KEY);
      return null;
    }

    return JSON.parse(cached);
  } catch {
    return null;
  }
}

export function saveCachedAuth(userData: AuthUser | null) {
  if (typeof window === 'undefined') return;
  try {
    if (userData) {
      localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(userData));
      localStorage.setItem(AUTH_CACHE_TIMESTAMP_KEY, Date.now().toString());
    } else {
      localStorage.removeItem(AUTH_CACHE_KEY);
      localStorage.removeItem(AUTH_CACHE_TIMESTAMP_KEY);
    }
  } catch (err) {
    console.error('Error saving cached auth:', err);
  }
}

// BroadcastChannel for cross-tab auth state sync
const authChannel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('corates-auth') : null;

function broadcastAuthChange() {
  authChannel?.postMessage({ type: 'auth-changed', timestamp: Date.now() });
}

// Shared cleanup logic for signout and revokeAllSessions
async function performSignoutCleanup() {
  const state = useAuthStore.getState();
  saveCachedAuth(null);
  state.setCachedUser(null);
  state.setCachedAvatarUrl(null);

  // Flip auth state to logged-out synchronously (before the async session
  // refetch below) so route guards redirect and auth-gated queries disable
  // immediately. Otherwise sessionUser lingers, a protected route's beforeLoad
  // still sees "logged in", and dependent queries refetch into 401s.
  state.setSessionData(null, false, state.sessionRefetch);

  // A chunk that fails to load (e.g. replaced by a deploy) must not stop the
  // rest of sign-out from clearing the session.
  try {
    const { clearAllData } = await import('@/primitives/db.js');
    await clearAllData();
  } catch (err) {
    console.warn('Failed to clear local data on sign-out:', err);
  }
  queryClient.clear();

  // Refetch session to clear it
  await state.sessionRefetch?.();
  broadcastAuthChange();
}

export const useAuthStore = create<AuthState & AuthActions>()((set, get) => ({
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  cachedUser: loadCachedAuth(),
  cachedAvatarUrl: null,
  authError: null,
  sessionUser: null,
  sessionLoading: true,
  sessionUnavailable: false,
  sessionRefetch: null,

  setOnline: online => set({ isOnline: online }),
  setCachedUser: user => set({ cachedUser: user }),
  setCachedAvatarUrl: url => set({ cachedAvatarUrl: url }),
  setAuthError: error => set({ authError: error }),
  setSessionData: (user, loading, refetch, unavailable = false) =>
    set({
      sessionUser: user,
      sessionLoading: loading,
      sessionRefetch: refetch,
      sessionUnavailable: unavailable,
    }),

  signup: async (email, password, name, role = null) => {
    try {
      set({ authError: null });
      const data = await authFetch(
        authClient.signUp.email({
          email,
          password,
          name,
          ...(role ? { role } : {}),
        } as Parameters<typeof authClient.signUp.email>[0]),
      );

      localStorage.setItem('pendingEmail', email);
      return data;
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  signin: async (email, password) => {
    try {
      set({ authError: null });
      const data = await authFetch(authClient.signIn.email({ email, password }));

      if ((data as Record<string, unknown>)?.twoFactorRedirect) {
        return { twoFactorRequired: true };
      }

      localStorage.removeItem('pendingEmail');
      saveLastLoginMethod(LOGIN_METHODS.EMAIL);
      broadcastAuthChange();
      return data;
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  signinWithGoogle: async (callbackPath, options) => {
    try {
      set({ authError: null });
      const path = callbackPath || '/dashboard';
      const base = (BASEPATH || '').replace(/\/$/, '');
      const callbackURL = `${window.location.origin}${base}${path}`;
      const errorURL = `${window.location.origin}${base}/signin`;

      saveLastLoginMethod(LOGIN_METHODS.GOOGLE);

      const data = await authFetch(
        authClient.signIn.social({
          provider: 'google',
          callbackURL,
          errorCallbackURL: errorURL,
          requestSignUp: options?.requestSignUp,
        }),
      );
      return data;
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  signinWithOrcid: async (callbackPath, options) => {
    try {
      set({ authError: null });
      const path = callbackPath || '/dashboard';
      const base = (BASEPATH || '').replace(/\/$/, '');
      const callbackURL = `${window.location.origin}${base}${path}`;
      const errorURL = `${window.location.origin}${base}/signin`;

      saveLastLoginMethod(LOGIN_METHODS.ORCID);

      const data = await authFetch(
        authClient.signIn.social({
          provider: 'orcid',
          callbackURL,
          errorCallbackURL: errorURL,
          requestSignUp: options?.requestSignUp,
        }),
      );
      return data;
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  sendSignInCode: async email => {
    try {
      set({ authError: null });
      await authFetch(authClient.emailOtp.sendVerificationOtp({ email, type: 'sign-in' }));
      localStorage.setItem('pendingEmail', email);
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  // Also creates the account for a new address; the code already proved ownership
  signinWithEmailCode: async (email, code) => {
    try {
      set({ authError: null });
      const data = await authFetch(authClient.signIn.emailOtp({ email, otp: code }));
      saveLastLoginMethod(LOGIN_METHODS.EMAIL_CODE);
      await get().sessionRefetch?.();
      return data;
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  signout: async () => {
    try {
      set({ authError: null });
      await authFetch(authClient.signOut());
      await performSignoutCleanup();
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  updateProfile: async data => {
    try {
      set({ authError: null });
      const updated = await authFetch(authClient.updateUser(data));
      await get().sessionRefetch?.();
      return updated;
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    try {
      set({ authError: null });
      await authFetch(authClient.changePassword({ currentPassword, newPassword }));
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  requestPasswordResetCode: async email => {
    try {
      set({ authError: null });
      await authFetch(authClient.forgetPassword.emailOtp({ email }));
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  resetPasswordWithCode: async (email, code, newPassword) => {
    try {
      set({ authError: null });
      await authFetch(
        authClient.emailOtp.resetPassword({ email, otp: code, password: newPassword }),
      );
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  sendVerificationCode: async email => {
    try {
      set({ authError: null });
      await authFetch(
        authClient.emailOtp.sendVerificationOtp({ email, type: 'email-verification' }),
      );
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  verifyEmailWithCode: async (email, code) => {
    try {
      set({ authError: null });
      await authFetch(authClient.emailOtp.verifyEmail({ email, otp: code }));
      saveLastLoginMethod(LOGIN_METHODS.EMAIL);
      await get().sessionRefetch?.();
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  requestOnboardingEmailCode: async email => {
    try {
      set({ authError: null });
      await authFetch(
        authClient.$fetch('/onboarding/request-email', { method: 'POST', body: { email } }),
      );
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  confirmOnboardingEmail: async (email, code) => {
    try {
      set({ authError: null });
      const data = await authFetch(
        authClient.$fetch<{ claimed: boolean }>('/onboarding/confirm-email', {
          method: 'POST',
          body: { email, code },
        }),
      );
      await get().sessionRefetch?.();
      return data ?? { claimed: false };
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  deleteAccount: async () => {
    try {
      set({ authError: null });
      await deleteMyAccount();
      localStorage.removeItem('pendingEmail');
      await authFetch(authClient.signOut());
      await performSignoutCleanup();
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete account';
      set({ authError: message });
      throw err;
    }
  },

  // 2FA

  enableTwoFactor: async password => {
    try {
      set({ authError: null });
      const data = await authFetch(authClient.twoFactor.enable({ password }));
      return data;
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  verifyTwoFactorSetup: async code => {
    try {
      set({ authError: null });
      const data = await authFetch(authClient.twoFactor.verifyTotp({ code }));
      broadcastAuthChange();
      return data;
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  disableTwoFactor: async password => {
    try {
      set({ authError: null });
      const data = await authFetch(authClient.twoFactor.disable({ password }));
      broadcastAuthChange();
      return data;
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  verifyTwoFactor: async code => {
    try {
      set({ authError: null });
      const data = await authFetch(authClient.twoFactor.verifyTotp({ code }));
      broadcastAuthChange();
      return data;
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  // Session management

  listActiveSessions: async () => {
    try {
      const data = await authFetch(_listSessions());
      return data || [];
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  revokeSessionByToken: async token => {
    try {
      await authFetch(_revokeSession({ token }));
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  revokeAllOtherSessions: async () => {
    try {
      await authFetch(_revokeOtherSessions());
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  revokeAllSessions: async () => {
    try {
      await authFetch(_revokeSessions());
      await performSignoutCleanup();
    } catch (err) {
      set({ authError: (err as Error).message });
      throw err;
    }
  },

  forceRefreshSession: async () => {
    try {
      await get().sessionRefetch?.();
    } catch (err) {
      console.warn('Force session refresh failed:', err);
      throw err;
    }
  },
}));

// Initialize: load cached avatar and prune expired entries
if (typeof window !== 'undefined') {
  const cachedAuth = loadCachedAuth();
  // The avatar cache brings Dexie and yjs with it; only returning users need
  // it, so anonymous page loads never fetch them or open IndexedDB.
  if (cachedAuth?.id) {
    void import('@/primitives/avatarCache.js').then(({ getCachedAvatar, pruneExpiredAvatars }) => {
      void getCachedAvatar(cachedAuth.id).then((dataUrl: string | null) => {
        if (dataUrl) useAuthStore.getState().setCachedAvatarUrl(dataUrl);
      });
      pruneExpiredAvatars();
    });
  }

  // Listen for online/offline
  window.addEventListener('online', () => useAuthStore.getState().setOnline(true));
  window.addEventListener('offline', () => useAuthStore.getState().setOnline(false));

  // Listen for auth changes from other tabs
  if (authChannel) {
    authChannel.addEventListener('message', event => {
      if (event.data?.type === 'auth-changed') {
        useAuthStore.getState().sessionRefetch?.();
      }
    });
  }
}

// Derived selectors (composing session + cached state)

// Only a settled answer from the server can sign a cached user out. While the
// check is still running, or failed without a 401, the cache stands in.
function cacheStandsIn(state: AuthState): boolean {
  return !!state.cachedUser && (state.sessionLoading || state.sessionUnavailable);
}

export function selectIsLoggedIn(state: AuthState): boolean {
  if (state.isOnline) {
    if (cacheStandsIn(state)) return true;
    return !!state.sessionUser;
  }
  return !!state.cachedUser;
}

export function selectIsAuthLoading(state: AuthState): boolean {
  if (!state.isOnline) return false;
  if (cacheStandsIn(state)) return false;
  return state.sessionLoading;
}

/**
 * Select the raw user object from the store.
 * Returns the sessionUser, cachedUser, or null depending on online/loading state.
 * Does NOT merge avatar URL -- use selectUserAvatarUrl separately to avoid
 * creating new object references on every render (which breaks useSyncExternalStore).
 */
export function selectUser(state: AuthState): AuthUser | null {
  if (state.isOnline) {
    const currentUser = state.sessionUser;
    if (currentUser) return currentUser;
    if (cacheStandsIn(state)) return state.cachedUser;
    return null;
  }
  return state.cachedUser;
}

export function selectUserAvatarUrl(state: AuthState): string | null {
  return state.cachedAvatarUrl;
}

export function selectTwoFactorEnabled(state: AuthState): boolean {
  const user = selectUser(state);
  return user?.twoFactorEnabled ?? false;
}
